// MACHINERY V2 (Stroje) — group-by vocabulary for Fleet V2 (M2.3, §9 D4).
//
// ONE table, consumed by the toolbar chips and by FleetV2Page's sectioning, so
// a dimension can never be offered in the control but unhandled by the grouper
// (or vice versa). Same discipline as fleetStatusFilters.ts.
//
// The i18n keys are the EXISTING live-grid ones (groupByType / groupByLocation
// / groupByLine / groupByDepartment / groupByAll) — the labels already ship in
// en + sk, so D4's carried dimensions arrive translated with no new strings.
//
// The grouping RULES themselves are not here: they live in
// fleetSelectors.groupMachinesClientSide, transcribed from the live grid, so
// V2's sections bucket exactly the way /machinery does today.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { MachineryV2GroupBy } from "../machineryV2UiSlice";
import { getMachineTypeLabel } from "../../../../shared/utils/machineUtils";
import { MachineRef } from "../../../../shared/utils/machineReferenceLabels";

export interface FleetGroupByOption {
  key: MachineryV2GroupBy;
  labelKey: string;
}

/** Type first — it is the dimension the artboard draws and the default. */
export const FLEET_GROUP_BY_OPTIONS: readonly FleetGroupByOption[] = [
  { key: "type", labelKey: "myMachinery:groupByType" },
  { key: "location", labelKey: "myMachinery:groupByLocation" },
  { key: "line", labelKey: "myMachinery:groupByLine" },
  { key: "department", labelKey: "myMachinery:groupByDepartment" },
  { key: "all", labelKey: "myMachinery:groupByAll" },
];

/**
 * Display title for a section key.
 *
 * groupMachinesClientSide emits three hard-coded ENGLISH sentinels for the
 * "no value" buckets ("Unknown" type, "Ungrouped", "All Machines") — inherited
 * verbatim from the live grid, where they are the same untranslated strings.
 * Fleet V2 groups entirely client-side, so EVERY title it renders comes from
 * that function and the sentinels are safe to translate at render time.
 *
 * The raw key is still what section collapse state is keyed on, so the title
 * can change with the UI language without a group silently re-expanding.
 *
 * Only these three literals are mapped; a real group named e.g. "Ungrouped"
 * would be translated too, which is cosmetic and matches what the live grid
 * would show anyway.
 *
 * Any other key goes through getMachineTypeLabel: under group-by-type the keys
 * ARE the English type names ("CNC Machine") because the grouping runs on
 * `type.name`; the label then takes the CODE from the group's first machine
 * (MACH-REF-I18N-1 §P4), with the closed type-name map as the fallback. For every
 * other dimension a key is a user-entered name that neither ever matches.
 */
const SECTION_TITLE_KEYS: Record<string, string> = {
  Unknown: "myMachinery:v2.fleet.groupUnknown",
  Ungrouped: "myMachinery:v2.fleet.groupUngrouped",
  "All Machines": "myMachinery:v2.fleet.groupAll",
};

export const sectionTitle = (
  key: string,
  translate: (k: string) => string,
  first?: { type?: MachineRef | null }
): string =>
  SECTION_TITLE_KEYS[key]
    ? translate(SECTION_TITLE_KEYS[key])
    : getMachineTypeLabel(first?.type?.name === key ? first.type : key, translate);
